import { useEffect, useRef } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { CircularProgress, Stack } from '@mui/material'
import { useGetDocument } from '@/service-library/hooks/documents'
import { useSetNotificationReadState } from '@/service-library/hooks/notifications'
import NotFound from './NotFound'

/**
 * Opens a document from a notification, push or email link. Those only know
 * the document id, so this looks up the document's project, then hands off
 * to the document page.
 */
export default function OpenDocumentPage() {
  const { documentId = '' } = useParams()
  const [searchParams] = useSearchParams()
  // Only browser push clicks pass this. The popover marks its own rows read.
  const notificationId = searchParams.get('notification_id')
  const { setNotificationReadState } = useSetNotificationReadState()
  const markedReadRef = useRef(false)

  useEffect(() => {
    if (!notificationId || markedReadRef.current) return
    markedReadRef.current = true
    setNotificationReadState({ id: notificationId, read: true })
  }, [notificationId, setNotificationReadState])

  const { document, isError } = useGetDocument({
    id: documentId,
    filters: { fields__only: 'id,project_id' },
    // A missing or hidden document, or one in another account, should land
    // on not-found right away.
    retry: false,
  })

  if (isError) return <NotFound />

  if (document) {
    return (
      <Navigate
        to={`/projects/${document.project_id}/documents?document_id=${document.id}`}
        replace
      />
    )
  }

  return (
    <Stack alignItems="center" sx={{ p: 4 }}>
      <CircularProgress />
    </Stack>
  )
}
